// ABOUTME: Day-of-week chip toggle component for Delivery Window configuration.
// ABOUTME: Renders 8 toggleable day chips (Mon–Sun + Holiday) using Fundamental Button.
import {Button} from '@hm/fundamental'
import {CountryConfigsTooltip} from './CountryConfigsTooltip'
import {mdiCheck, mdiPlus} from '@mdi/js'
import React, {useCallback} from 'react'

import {useTranslation} from '../../../../hooks/useTranslation'
import {DayChipToggleProps, DAYS, DayIdentifier} from '../types/deliveryWindow'

/** Canonical ordering — output arrays are always sorted in this order */
const DAY_LABELS: Record<DayIdentifier, string> = {
  monday: 'countryConfigurations.days.monday',
  tuesday: 'countryConfigurations.days.tuesday',
  wednesday: 'countryConfigurations.days.wednesday',
  thursday: 'countryConfigurations.days.thursday',
  friday: 'countryConfigurations.days.friday',
  saturday: 'countryConfigurations.days.saturday',
  sunday: 'countryConfigurations.days.sunday',
  holiday: 'countryConfigurations.days.holiday',
}

/**
 * Workaround: overrides Fundamental Button's `variantToggled.secondary` colors.
 * Fundamental currently uses `button_background_selected` instead of `button_background_toggled`.
 * @TODO Remove once Fundamental fixes `variantToggled.secondary` in Button/constants.ts
 * and uses `button_background_toggled` tokens. Track upstream fix before removing.
 */
const TOGGLED_COLOR_CLASS =
  'bg-button_background_toggled hover:bg-button_background_toggled_hover active:bg-button_background_toggled_selected text-foreground_inverted border-1px border-transparent'

const DISABLED_DAY_COLOR_CLASS =
  'bg-container_background text-foreground_faded border-1px border-border cursor-default'

export const DayChipToggle: React.FC<DayChipToggleProps> = ({value, onChange, days = DAYS, disabled = false, disabledDays = [], disabledTooltip, 'data-cy': dataCy}) => {
  const {t} = useTranslation()

  const handleToggle = useCallback(
    (day: DayIdentifier) => {
      if (disabled || disabledDays.includes(day)) return
      const isSelected = value.includes(day)
      const next = isSelected ? value.filter((d) => d !== day) : [...value, day]
      // Sort output in canonical DAYS order
      const sorted = DAYS.filter((d) => next.includes(d))
      onChange(sorted)
    },
    [value, onChange, disabled, disabledDays]
  )

  return (
    <div
      className="flex flex-wrap gap-2"
      role="group"
      aria-label={t('countryConfigurations.days.groupLabel')}
      data-cy={dataCy}
    >
      {days.map((day) => {
        const isSelected = value.includes(day)
        const isDayDisabled = disabled || disabledDays.includes(day)
        const isIndividuallyDisabled = !disabled && disabledDays.includes(day)

        const button = (
          <Button
            key={day}
            variant="secondary"
            size="small"
            type="button"
            toggled={isSelected}
            colorClass={isSelected ? TOGGLED_COLOR_CLASS : isIndividuallyDisabled ? DISABLED_DAY_COLOR_CLASS : undefined}
            iconPrefix={isSelected ? mdiCheck : mdiPlus}
            disabled={disabled}
            aria-disabled={isDayDisabled}
            aria-pressed={isSelected}
            tabIndex={isDayDisabled ? -1 : 0}
            onKeyDown={isDayDisabled ? (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') e.preventDefault() } : undefined}
            onClick={() => handleToggle(day)}
            className="!px-3"
          >
            {t(DAY_LABELS[day])}
          </Button>
        )

        if (isIndividuallyDisabled && !isSelected && disabledTooltip) {
          return (
            <CountryConfigsTooltip key={day} content={disabledTooltip} delayMs={300}>
              <span>{button}</span>
            </CountryConfigsTooltip>
          )
        }

        return button
      })}
    </div>
  )
}
